import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { MissionCard } from "../../components/MissionCard";
import { MissionCreateForm } from "../../components/MissionCreateForm";
import { missionCategory, MISSION_CATEGORIES } from "../../components/MissionPanel";
import { eur } from "../../lib/helpers";
import * as Icons from "lucide-react";
import { ICONS_BY_NAME } from "../../lib/icons";
import { toast } from "sonner";
import { useConfirm } from "../../components/ConfirmDialog";

export default function AdminModuleMissions() {
  const { confirm } = useConfirm();
  const { key } = useParams();
  const nav = useNavigate();
  const [module, setModule] = useState(null);
  const [missions, setMissions] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [tab, setTab] = useState("en_cours");
  const [search, setSearch] = useState("");
  const [fCollab, setFCollab] = useState("");
  const [fClient, setFClient] = useState("");
  const [sort, setSort] = useState("date_asc");
  const [newMission, setNewMission] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/missions").then((r) => setMissions(r.data.filter((m) => m.module_key === key)));
    api.get("/admin/modules").then((r) => { setModules(r.data); setModule(r.data.find((m) => m.key === key) || null); });
  }, [key]);

  useEffect(() => {
    load();
    api.get("/admin/collaborators").then((r) => setCollabs(r.data));
    api.get("/admin/clients").then((r) => setClients(r.data));
    api.get("/admin/projects").then((r) => setProjects(r.data));
  }, [load]);

  const filtered = missions.filter((m) => {
    if (fCollab && m.collaborator_id !== fCollab) return false;
    if (fClient && m.client_id !== fClient) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${m.type} ${m.collaborator_name || ""} ${m.client_name || ""} ${m.project_name || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const buckets = { a_venir: [], en_cours: [], terminee: [] };
  filtered.forEach((m) => buckets[missionCategory(m)].push(m));
  const list = [...buckets[tab]].sort((a, b) => {
    if (sort === "recent") return (b.created_at || "").localeCompare(a.created_at || "");
    const da = a.deadline || "", db = b.deadline || "";
    return sort === "date_desc" ? db.localeCompare(da) : da.localeCompare(db);
  });
  const hasFilters = search.trim() || fCollab || fClient;

  const deleteMission = async (m) => {
    if (!(await confirm({ title: `Supprimer définitivement la mission « ${m.type} » ?`, message: "Cette action est irréversible.", confirmLabel: "Supprimer", tone: "danger" }))) return;
    try { await api.delete(`/admin/missions/${m.id}`); toast.success("Mission supprimée"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };
  const recap = list.reduce((a, m) => {
    const f = m.collab_financials_eur || {};
    a.total += f.total_eur || 0; a.paid += f.paid_eur || 0; a.reste += f.reste_eur || 0; a.retard += m.overdue_pay_amount_eur || 0;
    return a;
  }, { total: 0, paid: 0, reste: 0, retard: 0 });

  return (
    <div>
      <button onClick={() => nav("/admin/modules")} data-testid="back-to-modules" className="text-sm text-marine/80 hover:text-marine inline-flex items-center gap-1 mb-3"><Icons.ChevronLeft size={16} className="text-marine" /> Tous les modules</button>
      <PageTitle overline="Module · Missions" title={module?.label || key} action={
        <button onClick={() => setNewMission(true)} data-testid="new-mission-btn" className="bg-marine text-white rounded-sm px-4 py-2.5 text-sm hover:bg-marine-hover flex items-center gap-2"><Icons.Plus size={16} /> Nouvelle mission</button>
      } />

      <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-marine/80" data-testid="mrecap-scope"><Icons.Wallet size={16} className="text-bordeaux" /> Récap financier · {MISSION_CATEGORIES[tab]?.label || "Missions"} ({list.length})</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="missions-pay-recap">
        <div className="bg-sable rounded-sm border border-marine/10 p-4">
          <div className="text-xs uppercase tracking-wider text-marine/80 mb-1 flex items-center gap-1"><Icons.Coins size={16} className="text-marine" /> Rémunération totale</div>
          <div className="font-display text-2xl text-marine" data-testid="mrecap-total">{eur(recap.total)}</div>
        </div>
        <div className="bg-sable rounded-sm border border-bleu/25 p-4">
          <div className="text-xs uppercase tracking-wider text-marine/80 mb-1 flex items-center gap-1"><Icons.CheckCircle2 size={16} className="text-marine" /> Déjà payé</div>
          <div className="font-display text-2xl text-bleu" data-testid="mrecap-paid">{eur(recap.paid)}</div>
        </div>
        <div className="bg-sable rounded-sm border border-bordeaux/25 p-4">
          <div className="text-xs uppercase tracking-wider text-marine/80 mb-1 flex items-center gap-1"><Icons.Clock size={16} className="text-marine" /> Reste à payer</div>
          <div className="font-display text-2xl text-bordeaux" data-testid="mrecap-reste">{eur(recap.reste)}</div>
        </div>
        <div className="bg-sable rounded-sm border border-rouge/25 p-4">
          <div className="text-xs uppercase tracking-wider text-marine/80 mb-1 flex items-center gap-1"><Icons.AlertTriangle size={16} className="text-marine" /> Dont en retard</div>
          <div className="font-display text-2xl text-rouge" data-testid="mrecap-retard">{eur(recap.retard)}</div>
        </div>
      </div>

      <div className="bg-sable rounded-sm border border-marine/10 p-4 mb-5" data-testid="mission-filters">
        <div className="relative mb-3">
          <Icons.Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-marine" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="mission-search" placeholder="Rechercher par mission, collaborateur, entreprise, projet..." className="w-full bg-creme border border-marine/15 rounded-sm pl-9 pr-3 py-2.5 text-sm text-marine focus:border-marine/50 outline-none" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Filtrer par collaborateur" value={fCollab} onChange={(e) => setFCollab(e.target.value)} data-testid="mission-filter-collab" className="bg-white border border-marine/15 rounded-sm px-3 py-2 text-sm text-marine focus:border-marine/50 outline-none">
            <option value="">Tous les collaborateurs</option>
            {collabs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select aria-label="Filtrer par entreprise" value={fClient} onChange={(e) => setFClient(e.target.value)} data-testid="mission-filter-client" className="bg-white border border-marine/15 rounded-sm px-3 py-2 text-sm text-marine focus:border-marine/50 outline-none">
            <option value="">Tous les entreprises</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select aria-label="Trier les missions" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="mission-sort" className="bg-white border border-marine/15 rounded-sm px-3 py-2 text-sm text-marine focus:border-marine/50 outline-none">
            <option value="date_asc">Échéance la plus proche</option>
            <option value="date_desc">Échéance la plus lointaine</option>
            <option value="recent">Plus récentes</option>
          </select>
          {hasFilters ? <button onClick={() => { setSearch(""); setFCollab(""); setFClient(""); }} data-testid="mission-filter-reset" className="text-xs px-3 py-2 text-rouge hover:underline inline-flex items-center gap-1"><Icons.X size={16} /> Réinitialiser</button> : null}
          <span className="ml-auto text-xs text-marine/80" data-testid="mission-count">{list.length} / {missions.length} mission(s)</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="mission-tabs">
        {Object.entries(MISSION_CATEGORIES).map(([k, meta]) => {
          const Ic = ICONS_BY_NAME[meta.icon] || Icons.Circle;
          const activeTab = tab === k;
          return (
            <button key={k} onClick={() => setTab(k)} data-testid={`mission-tab-${k}`}
              className={`px-4 py-1.5 rounded-sm text-sm border flex items-center gap-1.5 transition-colors ${activeTab ? "bg-marine text-white border-marine" : "border-marine/20 text-marine hover:bg-marine/5"}`}>
              <Ic size={16} style={activeTab ? {} : { color: meta.color }} /> {meta.label}
              <span className={`text-xs px-1.5 rounded-sm ${activeTab ? "bg-white/20" : "bg-sable border border-marine/30 text-marine"}`}>{buckets[k].length}</span>
            </button>
          );
        })}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mission-grid">
        {list.map((m) => <MissionCard key={m.id} m={m} onOpen={() => nav(`/admin/missions/${m.id}`)} onProject={(pid) => nav(`/admin/projets/${pid}`)} onDelete={deleteMission} />)}
        {missions.length === 0 && <p className="text-sm text-marine/80">Aucune mission dans ce module. Créez-en une pour démarrer.</p>}
        {missions.length > 0 && list.length === 0 && <p className="text-sm text-marine/80">Aucune mission dans cette catégorie.</p>}
      </div>

      {newMission && (
        <MissionCreateForm collabs={collabs} clients={clients} projects={projects} modules={modules} initialModule={key} onClose={() => setNewMission(false)} onCreated={load} />
      )}
    </div>
  );
}
